/**
 * GoldFire Network (https://goldfire.me)
 * Copyright (c) 2012 - 2020, GoldFire Studios, Inc.
 * http://goldfirestudios.com
 */

import Vue from 'vue';
import {sync} from 'vuex-router-sync';
import {createRouter} from './router';
import {createStore} from './store';
import App from './components/App.vue';
import {isMobile} from './utils';
import './assets/css/main.css';

// Expose a factory function that creates a fresh copy of
// store, router and app instances on each SSR request.
export function createApp() {
  // Create store and router instances.
  const store = createStore();
  const router = createRouter();

  // Sync the router with the vuex data store.
  sync(store, router);

  // Register a global custom directive called v-focus
  Vue.directive('focus', {
    inserted(el, binding) {
      // Don't auto-focus if this is IE because placeholders are broken.
      // Also, don't focus if this is mobile, to prevent odd keyboard behavior.
      let isIE = false;
      if (typeof window !== 'undefined' && typeof window.navigator !== 'undefined') {
        isIE = window.navigator.userAgent.indexOf('Trident/') > 0;
      }

      // Auto-focus the field.
      if (binding.value !== false && !isIE && !isMobile()) {
        el.focus();
      }
    },
  });

  // Setup Google Analytics tracking.
  if (process.env.NODE_ENV !== 'development' && typeof window !== 'undefined') {
    router.afterEach((to, from) => {
      if (typeof window !== 'undefined' && typeof ga === 'function' && to.path !== from.path) {
        ga('set', 'page', to.path);
        ga('send', 'pageview');
      }
    });
  }

  // Setup the root application.
  const app = new Vue({
    render: h => h(App),
    router,
    store,
  });

  return {app, router, store};
}
